import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { EmptyState } from "../ui/empty-state";
import { AlertTriangle, ArrowLeft, ChevronRight, FileCheck, Loader2, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { apiService } from "@/services/api-service";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Checkbox } from "../ui/checkbox";

interface WorkspaceInternalControlsProps {
  workspaceName: string;
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
  controlsListSection?: string; // The activeSection for the controls list view (e.g., "dynamic-workspace-controls:...")
}

type ControlSignoffSection = "prep_so" | "rev_so" | "conc_so" | "eqr_so" | "partner_so";

interface WorkspaceControl {
  controlId: string;
  controlName: string;
  typeOfControl: string;
  nature: string;
  rommIds: string[];
  workspace: string[];
  signoff: {
    prep_so: Array<{ name: string; metadata: string }>;
    rev_so: Array<{ name: string; metadata: string }>;
    conc_so: Array<{ name: string; metadata: string }>;
    eqr_so: Array<{ name: string; metadata: string }>;
    partner_so: Array<{ name: string; metadata: string }>;
  };
}

function getInitialsForSignoff(name: string): string {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function ControlSignoffBadge({
  label,
  signoffArray,
  onSignoffClick,
  disabled,
}: {
  label: string;
  signoffArray: Array<{ name: string; metadata: string }>;
  onSignoffClick: () => void;
  disabled: boolean;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const validEntries = (signoffArray || []).filter((e) => e?.name && String(e.name).trim() !== "");
  const hasEntries = validEntries.length > 0;
  const formatDate = (s: string) => {
    if (!s) return "";
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };
  return (
    <div className="relative inline-flex items-center gap-1">
      {hasEntries && (
        <div
          className="flex items-center gap-0.5"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {validEntries.slice(0, 3).map((entry, idx) => (
            <div
              key={idx}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium cursor-help border border-blue-500/30"
              title={`${entry.name} - ${formatDate(entry.metadata || "")}`}
            >
              {getInitialsForSignoff(entry.name)}
            </div>
          ))}
          {validEntries.length > 3 && (
            <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
              +{validEntries.length - 3}
            </div>
          )}
        </div>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={onSignoffClick}
        disabled={disabled}
        className={`h-6 w-6 p-0 ${disabled ? "text-white/30 cursor-not-allowed" : "text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"}`}
        title={disabled ? `You don't have permission to add ${label} signoff` : `Add ${label} signoff`}
      >
        <FileCheck className="h-3 w-3" />
      </Button>
      {showTooltip && hasEntries && (
        <div className="absolute z-10 bottom-full left-1/2 mb-2 min-w-[160px] max-w-xs -translate-x-1/2 rounded border border-white/10 bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
          <div className="font-semibold mb-1">{label} ({validEntries.length})</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {validEntries.map((entry, idx) => (
              <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                <div className="font-medium">{entry.name}</div>
                {entry.metadata && <div className="text-white/70 text-[10px]">{formatDate(entry.metadata)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkspaceInternalControls({
  workspaceName,
  onBack,
  setActiveSection,
  controlsListSection,
}: WorkspaceInternalControlsProps) {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();

  const [controls, setControls] = useState<WorkspaceControl[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [controlSignoffLoading, setControlSignoffLoading] = useState(false);
  const [selectedControlsForSignoff, setSelectedControlsForSignoff] = useState<Set<string>>(new Set());
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);


  const loadControlsForWorkspace = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!(window as any).sharePointAPI?.downloadRcmJson) {
        console.warn(
          "sharePointAPI.downloadRcmJson not available; cannot load Libraries_RCM.json",
        );
        setControls([]);
        return;
      }

      const res = await (window as any).sharePointAPI.downloadRcmJson();
      if (!res.success || !res.data) {
        console.error("downloadRcmJson failed:", res.error);
        setError("Failed to load controls from SharePoint.");
        setControls([]);
        return;
      }

      const data = res.data;
      const librariesRcm = data.libraries_RCM || data.libraries_rcm || {};
      const entries = Object.entries(librariesRcm) as [string, any][];

      const workspaceControls: WorkspaceControl[] = entries
        .map(([controlId, entry]) => {
          const rawWorkspace = entry.workspace || [];
          const wsArr = Array.isArray(rawWorkspace)
            ? rawWorkspace
            : rawWorkspace
            ? [rawWorkspace]
            : [];
          const signoff = entry.signoff || {};
          return {
            controlId,
            controlName: entry.control_name || entry.controlName || controlId,
            typeOfControl:
              entry.TypeOfControl || entry.typeOfControl || "Unknown",
            nature: entry.Nature || entry.subtype || "Unknown",
            rommIds: Array.isArray(entry.romm) ? entry.romm.map(String) : [],
            workspace: wsArr,
            signoff: {
              prep_so: Array.isArray(signoff.prep_so) ? signoff.prep_so : [],
              rev_so: Array.isArray(signoff.rev_so) ? signoff.rev_so : [],
              conc_so: Array.isArray(signoff.conc_so) ? signoff.conc_so : [],
              eqr_so: Array.isArray(signoff.eqr_so) ? signoff.eqr_so : [],
              partner_so: Array.isArray(signoff.partner_so) ? signoff.partner_so : [],
            },
          } as WorkspaceControl;
        })
        .filter((c) => c.workspace.includes(workspaceName));

      setControls(workspaceControls);
    } catch (e) {
      console.error(
        "Error loading controls for workspace from Libraries_RCM.json:",
        e,
      );
      setError("Unexpected error loading controls.");
      setControls([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceName]);

  useEffect(() => {
    loadControlsForWorkspace();
  }, [loadControlsForWorkspace]);

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch (err) {
        console.error("Error loading user engagement role for control signoff:", err);
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  const canControlSignoffSection = (section: ControlSignoffSection): boolean => {
    if (!user) return false;
    const engagementRole = userEngagementRole;
    const userType = user.type;
    switch (section) {
      case "prep_so":
        return (
          engagementRole === "associate" ||
          engagementRole === "article" ||
          userType === "associate" ||
          userType === "article"
        );
      case "rev_so":
        return (
          engagementRole === "engagement_manager" ||
          userType === "manager" ||
          userType === "associate" ||
          engagementRole === "associate"
        );
      case "conc_so":
        return userType === "manager" || userType === "partner";
      case "eqr_so":
        return engagementRole === "eqr_partner" || engagementRole === "eqr_manager";
      case "partner_so":
        return engagementRole === "engagement_partner";
      default:
        return false;
    }
  };

  const isControlPrepCompleted = (signoff: WorkspaceControl["signoff"]): boolean => {
    if (!signoff || !Array.isArray(signoff.prep_so)) return false;
    return signoff.prep_so.some((entry) => entry?.name && String(entry.name).trim() !== "");
  };

  const handleControlSignoff = async (controlId: string, section: ControlSignoffSection) => {
    if (!user || !user.user_name) return;
    try {
      setControlSignoffLoading(true);
      if (
        !(window as any).sharePointAPI?.downloadRcmJson ||
        !(window as any).sharePointAPI?.uploadRcmJson
      ) {
        alert("SharePoint RCM APIs are not available.");
        return;
      }
      const rcmResult = await (window as any).sharePointAPI.downloadRcmJson();
      if (!rcmResult?.success || !rcmResult.data) {
        alert(rcmResult?.error || "Failed to load Libraries_RCM.json");
        return;
      }
      const rcmData = rcmResult.data;
      if (!rcmData.libraries_RCM) rcmData.libraries_RCM = {};
      if (!rcmData.libraries_RCM[controlId]) {
        alert("Control not found in Libraries_RCM.json");
        return;
      }
      const entry = rcmData.libraries_RCM[controlId];
      if (!entry.signoff) {
        entry.signoff = {
          prep_so: [],
          rev_so: [],
          conc_so: [],
          eqr_so: [],
          partner_so: [],
        };
      }
      const signoffArr: any[] = Array.isArray(entry.signoff[section]) ? entry.signoff[section] : [];
      signoffArr.push({ name: user.user_name, metadata: new Date().toISOString() });
      entry.signoff[section] = signoffArr;

      const uploadResult = await (window as any).sharePointAPI.uploadRcmJson(rcmData);
      if (!uploadResult?.success) {
        alert(uploadResult?.error || "Failed to save control signoff");
        return;
      }
      await loadControlsForWorkspace();
    } catch (e: any) {
      console.error("Error updating control signoff:", e);
      alert(e?.message || "Unexpected error while updating control signoff");
    } finally {
      setControlSignoffLoading(false);
    }
  };

  const handleBulkControlSignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedControlsForSignoff.size === 0) return;
    const ids = Array.from(selectedControlsForSignoff);
    try {
      setControlSignoffLoading(true);
      for (const controlId of ids) {
        await handleControlSignoff(controlId, section);
      }
      setSelectedControlsForSignoff(new Set());
    } finally {
      setControlSignoffLoading(false);
    }
  };

  const handleControlClick = (control: WorkspaceControl) => {
    try {
      const controlId = control.controlId;
      
      if (!setActiveSection) {
        console.error('[WorkspaceInternalControls] ERROR: setActiveSection is not available!');
        alert('ERROR: Navigation function not available. Cannot open control.');
        return;
      }
      
      // Store where we came from so we can return here when closing the control
      if (controlsListSection) {
        sessionStorage.setItem('control-view-referrer', controlsListSection);
      }
      
      // Just update activeSection - let the main router handle rendering InternalControlView
      const newSection = `internal-control:${controlId}:control-summary`;
      setActiveSection(newSection);
    } catch (err) {
      console.error("[WorkspaceInternalControls] Error opening control:", err);
      alert(`ERROR: ${err}`);
    }
  };

  return (
    <div className="space-y-6 text-white">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            Internal Controls – {workspaceName}
          </h2>
          <p className="text-sm text-muted-foreground">
            Controls linked to this workspace.
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        )}
      </div>

      {loading ? (
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-muted-foreground">
              Loading internal controls from SharePoint...
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-red-500/40 bg-red-500/10">
          <CardContent className="flex items-center gap-2 p-4 text-sm text-red-200">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </CardContent>
        </Card>
      ) : controls.length === 0 ? (
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <EmptyState
              icon={<Shield className="h-8 w-8" />}
              title="No internal controls are currently linked to this workspace."
              description={`Create controls in the Internal Control Library and ensure their workspace includes "${workspaceName}" to see them here.`}
              centered
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Controls</h3>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {controls.length} control(s)
              </Badge>
              {!isArchivalMode && selectedControlsForSignoff.size > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={controlSignoffLoading}
                      className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                    >
                      {controlSignoffLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileCheck className="h-4 w-4 mr-2" />
                      )}
                      Sign off on selected ({selectedControlsForSignoff.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                    {(() => {
                      const selected = Array.from(selectedControlsForSignoff);
                      const allHavePrep = selected.length > 0 && selected.every((id) => {
                        const c = controls.find((x) => x.controlId === id);
                        return c && isControlPrepCompleted(c.signoff);
                      });
                      const canPrep = canControlSignoffSection("prep_so");
                      const canRev = canControlSignoffSection("rev_so") && allHavePrep;
                      const canConc = canControlSignoffSection("conc_so") && allHavePrep;
                      const canEqr = canControlSignoffSection("eqr_so") && allHavePrep;
                      const canPartner = canControlSignoffSection("partner_so") && allHavePrep;
                      return (
                        <>
                          <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkControlSignoff("prep_so")}>Preparer</DropdownMenuItem>
                          <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkControlSignoff("rev_so")}>Reviewer</DropdownMenuItem>
                          <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkControlSignoff("conc_so")}>Concurrent</DropdownMenuItem>
                          <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkControlSignoff("eqr_so")}>EQR</DropdownMenuItem>
                          <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkControlSignoff("partner_so")}>Partner</DropdownMenuItem>
                        </>
                      );
                    })()}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {/* Header row with column headings (same as Control Library) */}
            {!controlSignoffLoading && (
              <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                {!isArchivalMode && (
                  <div className="w-10 shrink-0 flex items-center justify-center">
                    <Checkbox
                      checked={controls.length > 0 && controls.every((c) => selectedControlsForSignoff.has(c.controlId))}
                      onCheckedChange={() => {
                        if (controls.length > 0 && controls.every((c) => selectedControlsForSignoff.has(c.controlId))) {
                          setSelectedControlsForSignoff(new Set());
                        } else {
                          setSelectedControlsForSignoff(new Set(controls.map((c) => c.controlId)));
                        }
                      }}
                      aria-label="Select all controls"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                  Control
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
                </div>
                <div className="w-8 flex-shrink-0" />
              </div>
            )}

            {controls.map((control) => {
              const signoff = control.signoff;
              const prepCompleted = isControlPrepCompleted(signoff);
              const canPrep = canControlSignoffSection("prep_so");
              const canRev = canControlSignoffSection("rev_so") && prepCompleted;
              const canConc = canControlSignoffSection("conc_so") && prepCompleted;
              const canEqr = canControlSignoffSection("eqr_so") && prepCompleted;
              const canPartner = canControlSignoffSection("partner_so") && prepCompleted;

              return (
                <div
                  key={control.controlId}
                  className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 p-3 hover:border-white/20 hover:bg-white/10"
                >
                  {!isArchivalMode && (
                    <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedControlsForSignoff.has(control.controlId)}
                        onCheckedChange={() => {
                          setSelectedControlsForSignoff((prev) => {
                            const next = new Set(prev);
                            if (next.has(control.controlId)) next.delete(control.controlId);
                            else next.add(control.controlId);
                            return next;
                          });
                        }}
                        aria-label={`Select ${control.controlId}`}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={() => handleControlClick(control)}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium text-white truncate">
                        {control.controlId}
                      </span>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {control.typeOfControl}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {control.nature}
                      </Badge>
                    </div>
                    <p className="text-xs text-white/80 truncate">
                      {control.controlName}
                    </p>
                    {control.rommIds.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {control.rommIds.slice(0, 3).map((rommId, idx) => (
                          <Badge key={idx} variant="outline" className="text-[9px]">
                            {rommId}
                          </Badge>
                        ))}
                        {control.rommIds.length > 3 && (
                          <span className="text-[9px] text-white/50">+{control.rommIds.length - 3}</span>
                        )}
                      </div>
                    )}
                  </button>
                  {!controlSignoffLoading && (
                    <div className="flex items-center gap-4 flex-shrink-0">
                      {isArchivalMode ? (
                        <>
                          <div className="w-24 flex justify-center">
                            {signoff.prep_so && signoff.prep_so.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.prep_so.slice(0, 3).map((entry, i) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.prep_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.prep_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="w-24 flex justify-center">
                            {signoff.rev_so && signoff.rev_so.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.rev_so.slice(0, 3).map((entry, i) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.rev_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.rev_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="w-24 flex justify-center">
                            {signoff.conc_so && signoff.conc_so.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.conc_so.slice(0, 3).map((entry, i) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.conc_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.conc_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="w-24 flex justify-center">
                            {signoff.eqr_so && signoff.eqr_so.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.eqr_so.slice(0, 3).map((entry, i) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.eqr_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.eqr_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="w-24 flex justify-center">
                            {signoff.partner_so && signoff.partner_so.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.partner_so.slice(0, 3).map((entry, i) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.partner_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.partner_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="Preparer"
                              signoffArray={signoff.prep_so || []}
                              onSignoffClick={() => handleControlSignoff(control.controlId, "prep_so")}
                              disabled={!canPrep}
                            />
                          </div>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="Reviewer"
                              signoffArray={signoff.rev_so || []}
                              onSignoffClick={() => handleControlSignoff(control.controlId, "rev_so")}
                              disabled={!canRev}
                            />
                          </div>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="Concurrent"
                              signoffArray={signoff.conc_so || []}
                              onSignoffClick={() => handleControlSignoff(control.controlId, "conc_so")}
                              disabled={!canConc}
                            />
                          </div>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="EQR"
                              signoffArray={signoff.eqr_so || []}
                              onSignoffClick={() => handleControlSignoff(control.controlId, "eqr_so")}
                              disabled={!canEqr}
                            />
                          </div>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="Partner"
                              signoffArray={signoff.partner_so || []}
                              onSignoffClick={() => handleControlSignoff(control.controlId, "partner_so")}
                              disabled={!canPartner}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="w-8 flex-shrink-0 flex justify-end">
                    <ChevronRight className="h-4 w-4 text-white/40" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
